import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BarChart, Bar, Cell, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie } from 'recharts';
import { Play, Square, Radio, Wifi, Loader2, Heart, Eye, Hash, Users, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api';
import { PC, PL, SC, fmtNum, ALL_PLATFORMS } from './types';

// Ported from EnhancedSocialStream.tsx's "Live Monitor" mode — a genuinely
// different, intentionally ephemeral capability (watch live for N minutes
// via SSE, no persistence) from the durable keyword-monitoring streams
// elsewhere in this module. Re-skinned to the shared dark-terminal palette;
// same functional behavior (session persisted to sessionStorage so a reload
// doesn't lose an in-progress watch, per-poll sentiment history, per-term
// stats, rule-based narrative summary — no LLM call, just a template).

const SESSION_KEY = 'sycek_live_monitor_v1';

export const LiveMonitorTab: React.FC = () => {
  const [monitorTopic, setMonitorTopic] = useState('');
  const [monitorPlatforms, setMonitorPlatforms] = useState<string[]>(['twitter']);
  const [monitorDuration, setMonitorDuration] = useState(300);
  const [streamStatus, setStreamStatus] = useState<'idle' | 'connecting' | 'live' | 'stopped'>('idle');
  const [streamedPosts, setStreamedPosts] = useState<any[]>([]);
  const [streamInitData, setStreamInitData] = useState<any>(null);
  const [streamProgress, setStreamProgress] = useState<{ remaining: number; total_seen: number; poll_count: number } | null>(null);
  const [sentCounts, setSentCounts] = useState({ positive: 0, neutral: 0, negative: 0 });
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'positive' | 'negative' | 'neutral'>('all');
  const [sentimentHistory, setSentimentHistory] = useState<Array<{ poll: number; pos: number; neu: number; neg: number }>>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const isMonitoring = streamStatus === 'live' || streamStatus === 'connecting';

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.posts?.length) {
          setStreamedPosts(s.posts);
          setStreamInitData(s.initData || null);
          setSentCounts(s.sentCounts || { positive: 0, neutral: 0, negative: 0 });
          setSentimentHistory(s.sentHistory || []);
          setStreamProgress(s.progress || null);
          setMonitorTopic(s.topic || '');
          setStreamStatus('stopped');
        }
      }
    } catch { /* ignore corrupt session */ }
    return () => { eventSourceRef.current?.close(); };
  }, []);

  const saveSession = (posts: any[], initData: any, sc: any, history: any[], progress: any, topic: string) => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ posts: posts.slice(0, 300), initData, sentCounts: sc, sentHistory: history, progress, topic })); } catch { /* ignore quota */ }
  };

  const startMonitoring = () => {
    if (!monitorTopic.trim() || isMonitoring) return;
    let localPosts: any[] = [];
    let localSent = { positive: 0, neutral: 0, negative: 0 };
    let localHistory: any[] = [];
    let localInitData: any = null;
    let localProgress: any = null;
    setStreamedPosts([]); setStreamInitData(null); setStreamProgress(null);
    setSentCounts(localSent); setSentimentHistory([]); setStreamStatus('connecting');

    const es = apiClient.createTopicStream({
      topic: monitorTopic, platforms: monitorPlatforms, duration: monitorDuration,
      onInit: (data: any) => { localInitData = data; setStreamInitData(data); setStreamStatus('live'); },
      onPosts: (data: any) => {
        const posts: any[] = data.posts || [];
        localPosts = [...posts, ...localPosts].slice(0, 500);
        setStreamedPosts(localPosts);
        const sc = data.sentiment_counts || {};
        localSent = { positive: localSent.positive + (sc.positive || 0), neutral: localSent.neutral + (sc.neutral || 0), negative: localSent.negative + (sc.negative || 0) };
        setSentCounts(localSent);
        localHistory = [...localHistory, { poll: data.poll_count || localHistory.length + 1, pos: sc.positive || 0, neu: sc.neutral || 0, neg: sc.negative || 0 }];
        setSentimentHistory(localHistory);
        saveSession(localPosts, localInitData, localSent, localHistory, localProgress, monitorTopic);
      },
      onHeartbeat: (data: any) => { localProgress = { remaining: data.remaining, total_seen: data.total_seen, poll_count: data.poll_count }; setStreamProgress(localProgress); },
      onDone: () => { setStreamStatus('stopped'); eventSourceRef.current?.close(); eventSourceRef.current = null; saveSession(localPosts, localInitData, localSent, localHistory, localProgress, monitorTopic); },
      onError: () => { setStreamStatus('stopped'); eventSourceRef.current?.close(); eventSourceRef.current = null; },
    });
    eventSourceRef.current = es;
  };

  const stopMonitoring = () => { eventSourceRef.current?.close(); eventSourceRef.current = null; setStreamStatus('stopped'); };

  const filteredPosts = useMemo(() =>
    streamedPosts.filter(p => sentimentFilter === 'all' || p.sentiment === sentimentFilter),
    [streamedPosts, sentimentFilter]);

  const wordCloud = useMemo(() => {
    const freq: Record<string, number> = {};
    for (const p of streamedPosts) for (const h of (p.hashtags || [])) if (h?.length > 1) freq[h.toLowerCase()] = (freq[h.toLowerCase()] || 0) + 1;
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [streamedPosts]);

  const topAuthors = useMemo(() => {
    const map: Record<string, { handle: string; platform: string; engagement: number; posts: number }> = {};
    for (const p of streamedPosts) {
      const key = p.author || 'unknown';
      if (!map[key]) map[key] = { handle: key, platform: p.platform, engagement: 0, posts: 0 };
      map[key].engagement += (p.views || 0) + (p.likes || 0) * 10 + (p.retweets || 0) * 5;
      map[key].posts++;
    }
    return Object.values(map).sort((a, b) => b.engagement - a.engagement).slice(0, 6);
  }, [streamedPosts]);

  const narrative = useMemo(() => {
    if (!streamedPosts.length || !streamInitData) return null;
    const total = sentCounts.positive + sentCounts.neutral + sentCounts.negative;
    if (total === 0) return null;
    const dominant = sentCounts.negative > sentCounts.positive && sentCounts.negative > sentCounts.neutral ? 'negative'
      : sentCounts.positive > sentCounts.neutral ? 'positive' : 'neutral';
    const domPct = Math.round((sentCounts[dominant] / total) * 100);
    const polls = streamProgress?.poll_count || sentimentHistory.length;
    return `Intelligence stream on "${streamInitData.topic}": ${streamedPosts.length} posts over ${polls} poll${polls !== 1 ? 's' : ''}. ` +
      `${dominant === 'negative' ? `Negative discourse dominates at ${domPct}%.` : dominant === 'positive' ? `Positive sentiment leads at ${domPct}%.` : `Discourse is predominantly neutral at ${domPct}%.`}` +
      (wordCloud.length > 0 ? ` Trending: ${wordCloud.slice(0, 4).map(([w]) => '#' + w).join(', ')}.` : '');
  }, [streamedPosts, streamInitData, sentCounts, streamProgress, sentimentHistory, wordCloud]);

  const sentimentPie = [
    { name: 'Positive', value: sentCounts.positive, fill: '#22c55e' },
    { name: 'Neutral', value: sentCounts.neutral, fill: '#6b7280' },
    { name: 'Negative', value: sentCounts.negative, fill: '#ef4444' },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-5">
      {/* Config + status bar */}
      <div className="rounded-lg p-4 flex items-center gap-3 flex-wrap" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
        <Input placeholder="Topic to watch live..." value={monitorTopic} onChange={e => setMonitorTopic(e.target.value)}
          disabled={isMonitoring} className="h-8 text-xs flex-1 min-w-[200px]" style={{ background: '#111', border: '1px solid #222', color: '#e5e7eb' }} />
        <div className="flex gap-1">
          {ALL_PLATFORMS.filter(p => ['twitter', 'instagram', 'facebook'].includes(p)).map(p => (
            <button key={p} disabled={isMonitoring}
              onClick={() => setMonitorPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
              className="text-[10px] px-2 py-1 rounded font-mono"
              style={{ background: monitorPlatforms.includes(p) ? `${PC[p]}20` : '#111', color: monitorPlatforms.includes(p) ? PC[p] : '#4b5563', border: `1px solid ${monitorPlatforms.includes(p) ? PC[p] + '50' : '#222'}` }}>
              {PL[p]}
            </button>
          ))}
        </div>
        <select value={monitorDuration} disabled={isMonitoring} onChange={e => setMonitorDuration(Number(e.target.value))}
          className="h-8 text-[11px] rounded px-2" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
          <option value={120}>2 min</option>
          <option value={300}>5 min</option>
          <option value={600}>10 min</option>
        </select>
        {!isMonitoring ? (
          <Button onClick={startMonitoring} disabled={!monitorTopic.trim()} className="h-8 text-xs font-mono"
            style={{ background: '#00ff0015', border: '1px solid #00ff0050', color: '#00ff00' }}>
            <Play size={11} className="mr-1.5" /> Start watching
          </Button>
        ) : (
          <Button onClick={stopMonitoring} className="h-8 text-xs font-mono"
            style={{ background: '#ef444415', border: '1px solid #ef444450', color: '#ef4444' }}>
            <Square size={11} className="mr-1.5" /> Stop
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2 text-[10px] font-mono">
          {streamStatus === 'connecting' && <><Loader2 size={11} className="animate-spin" style={{ color: '#eab308' }} /><span style={{ color: '#eab308' }}>Connecting...</span></>}
          {streamStatus === 'live' && <><Wifi size={11} style={{ color: '#00ff00' }} className="animate-pulse" /><span style={{ color: '#00ff00' }}>LIVE — {streamProgress?.remaining ?? '…'}s remaining</span></>}
          {streamStatus === 'stopped' && <span className="text-gray-600">Stopped — {streamedPosts.length} posts collected</span>}
        </div>
      </div>

      {streamedPosts.length > 0 && (
        <>
          {narrative && (
            <div className="rounded-lg p-4 flex items-start gap-2" style={{ background: '#00aaff08', border: '1px solid #00aaff20' }}>
              <Sparkles size={13} style={{ color: '#00aaff', flexShrink: 0, marginTop: 2 }} />
              <p className="text-xs text-gray-300 leading-relaxed">{narrative}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-2">Sentiment</p>
              <ResponsiveContainer width="100%" height={100}>
                <PieChart>
                  <Pie data={sentimentPie} dataKey="value" cx="50%" cy="50%" innerRadius={24} outerRadius={42}>
                    {sentimentPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-2 flex items-center gap-1"><Hash size={10} /> Top hashtags</p>
              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={wordCloud.map(([w, c]) => ({ word: w, count: c }))}>
                  <XAxis dataKey="word" tick={{ fontSize: 8, fill: '#4b5563' }} />
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 11 }} />
                  <Bar dataKey="count" fill="#a855f7" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-2 flex items-center gap-1"><Users size={10} /> Top voices</p>
              <div className="space-y-1.5">
                {topAuthors.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400 truncate">@{a.handle}</span>
                    <span className="text-gray-600 font-mono">{a.posts}p</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-600 font-mono uppercase">Filter:</span>
            {(['all', 'positive', 'negative', 'neutral'] as const).map(f => (
              <button key={f} onClick={() => setSentimentFilter(f)}
                className="px-2 py-0.5 rounded text-[10px] font-mono"
                style={{ background: sentimentFilter === f ? '#00ff0015' : '#111', border: `1px solid ${sentimentFilter === f ? '#00ff0040' : '#222'}`, color: sentimentFilter === f ? '#00ff00' : '#4b5563' }}>
                {f}
              </button>
            ))}
            <span className="ml-auto text-[10px] text-gray-600 font-mono">{filteredPosts.length} / {streamedPosts.length}</span>
          </div>

          <div className="space-y-2">
            {filteredPosts.slice(0, 50).map((p, i) => (
              <div key={p.id ?? i} className="rounded-lg p-3 flex items-start justify-between gap-3" style={{ background: '#0f0f0f', border: `1px solid ${PC[p.platform] ?? PC.default}20` }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono font-bold" style={{ color: PC[p.platform] ?? PC.default }}>{PL[p.platform] ?? p.platform}</span>
                    <span className="text-[10px] text-gray-500">@{p.author}</span>
                    <div className="w-1 h-1 rounded-full" style={{ background: SC[p.sentiment] ?? SC.unknown }} />
                  </div>
                  <p className="text-[11px] text-gray-300 line-clamp-2">{p.text}</p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-600 flex-shrink-0">
                  {p.likes > 0 && <span className="flex items-center gap-0.5"><Heart size={9} />{fmtNum(p.likes)}</span>}
                  {p.views > 0 && <span className="flex items-center gap-0.5"><Eye size={9} />{fmtNum(p.views)}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {streamedPosts.length === 0 && streamStatus === 'idle' && (
        <div className="flex flex-col items-center py-16">
          <Radio size={32} className="text-gray-700 mb-3" />
          <p className="text-sm text-gray-600">Watch a topic live for a few minutes — ephemeral, not persisted.</p>
          <p className="text-xs text-gray-700 mt-1">For durable keyword monitoring, use the sidebar's Keyword Monitor instead.</p>
        </div>
      )}
    </div>
  );
};

export default LiveMonitorTab;
